import { easternIsoDay } from '../data/taskRules';
import { addCalendarDays, easternMidnightIso } from '../data/timeZones';
import { MAX_KPI_ID, MAX_KPI_VALUE, MAX_KPIS } from './kpiSummaryShape';
import { isQuestionVisible, type AnswerMap, type TemplateDefinition } from './template';

/**
 * Visit KPIs (owner rulings 2026-10-20): the numbers a DM types on a visit
 * (Occupancy %, Move-ins, AutoPay % …) are "overall performance over time".
 * Every NUMBER or PERCENT question of a template is a KPI — generic, so a
 * KPI added later in the builder shows up by itself, on any template.
 *
 * Like the failure summary, the values are written to the Audits row at
 * submit (Audits.KpiSummary), so Analytics reads one row per audit and never
 * scans AuditResponses:
 *
 *   { "v": 1, "n": { "<questionId>": <number> } }
 *
 * Pure; the app, the server's checks and the backfill share it.
 */

export interface KpiSummary {
  v: 1;
  n: Record<string, number>;
}

export { MAX_KPIS };

const ID = /^[a-z0-9][a-z0-9_-]*$/;

/** defineProperty: a hand-edited "__proto__" id must not replace the prototype. */
function put(obj: Record<string, number>, key: string, value: number) {
  Object.defineProperty(obj, key, { value, enumerable: true, writable: true, configurable: true });
}

const isKpiType = (t: string) => t === 'number' || t === 'percent';

/** The KPI values of one audit: visible number/percent questions with a finite answer. */
export function summarizeKpis(def: TemplateDefinition, answers: AnswerMap): KpiSummary {
  const out: KpiSummary = { v: 1, n: {} };
  for (const section of def.sections ?? []) {
    for (const q of section.questions ?? []) {
      if (!isKpiType(q.type) || !isQuestionVisible(def, section, q, answers)) continue;
      // The server's shape check (kpiSummaryShape.ts) — never write what it refuses.
      if (q.id.length > MAX_KPI_ID || Object.keys(out.n).length >= MAX_KPIS) continue;
      const value = Object.prototype.hasOwnProperty.call(answers, q.id) ? answers[q.id] : undefined;
      if (typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= MAX_KPI_VALUE) put(out.n, q.id, value);
    }
  }
  return out;
}

/** Does this template have any KPI (number/percent) question? */
export function hasKpis(def: TemplateDefinition | null | undefined): boolean {
  return (def?.sections ?? []).some((s) => (s.questions ?? []).some((q) => isKpiType(q.type)));
}

/**
 * Read a stored summary defensively (the column is written by the conductor):
 * malformed parts are dropped, never thrown. null = no summary on the row.
 */
export function parseKpiSummary(raw: string | null | undefined): KpiSummary | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || (parsed as { v?: unknown }).v !== 1) return null;
  const src = (parsed as { n?: unknown }).n;
  const out: KpiSummary = { v: 1, n: {} };
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    let kept = 0;
    for (const [k, v] of Object.entries(src as Record<string, unknown>)) {
      if (kept >= MAX_KPIS) break;
      if (!ID.test(k) || typeof v !== 'number' || !Number.isFinite(v)) continue;
      put(out.n, k, v);
      kept++;
    }
  }
  return out;
}

// --- Columns -------------------------------------------------------------------

export interface KpiColumn {
  id: string;
  label: string;
  type: 'number' | 'percent';
  section: string;
}

/**
 * A template family's KPI columns, in the newest non-draft version's
 * question order (its wording wins — ids are stable across versions);
 * KPIs only an older version still has follow at the end, so history keeps
 * its columns. Drafts never supply columns.
 */
export function kpiColumns(
  rows: readonly { version: number; status?: string; definition: TemplateDefinition | null }[],
): KpiColumn[] {
  const usable = rows.filter((r) => r.status !== 'Draft' && r.definition);
  const out: KpiColumn[] = [];
  const seen = new Set<string>();
  for (const r of [...usable].sort((a, b) => b.version - a.version)) {
    for (const s of r.definition!.sections ?? []) {
      for (const q of s.questions ?? []) {
        if (!isKpiType(q.type) || seen.has(q.id)) continue;
        seen.add(q.id);
        out.push({ id: q.id, label: q.text, type: q.type as 'number' | 'percent', section: s.title });
      }
    }
  }
  return out;
}

// --- Ranges --------------------------------------------------------------------

/** Inclusive start, exclusive end (ISO times). */
export interface KpiRange {
  start: string;
  end: string;
}

/**
 * The same number of CALENDAR days right before `range` (the "change vs
 * previous" baseline). Counted in Eastern calendar days, not milliseconds,
 * so a range spanning a daylight-saving change still starts the baseline at
 * Eastern midnight (QA review 2026-10-20; Eastern since 2026-10-28).
 */
export function previousRange(range: KpiRange): KpiRange {
  const startDay = easternIsoDay(new Date(range.start));
  const days = Math.round((new Date(range.end).getTime() - new Date(range.start).getTime()) / 86_400_000);
  return { start: easternMidnightIso(addCalendarDays(startDay, -days)), end: range.start };
}

/** A company-calendar (Eastern) day range [from, to] (both yyyy-mm-dd, inclusive) as ISO times. */
export function dayRange(from: string, to: string): KpiRange | null {
  // A real calendar day ("2026-02-31" would roll over to Mar 3 — QA 2026-10-28).
  const ok = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d) && addCalendarDays(d, 0) === d;
  if (!ok(from) || !ok(to) || to < from) return null;
  return { start: easternMidnightIso(from), end: easternMidnightIso(addCalendarDays(to, 1)) };
}

// --- Averages ------------------------------------------------------------------

export interface KpiAudit {
  storeId: number;
  submittedAt: string;
  /** When the visit started — the day its month-to-date values were typed (budgets compare on it; B8). */
  startedAt?: string;
  kpis: KpiSummary | null;
}

export interface KpiCell {
  /** Average over the range's visits that recorded it; null = none did. */
  avg: number | null;
  /** How many visits recorded it in the range. */
  count: number;
  /** Average over the previous range; null = none (or no previous range). */
  prevAvg: number | null;
  /** avg − prevAvg (percent = points); null unless both exist. */
  change: number | null;
  /**
   * B8 budgets: over the range's visits that HAD a budget (expectedOf), the
   * average value and the average expected value (month-to-date budgets
   * pro-rated to each visit's day). null = no visit had one.
   */
  budget?: { avgValue: number; avgExpected: number; n: number } | null;
}

/** Largest number KPI value counted (a typo like 1e300 must not swamp an average). */
export const MAX_KPI_NUMBER = 1e9;

/** Values an average may use: percents 0–100, numbers within ±MAX_KPI_NUMBER (security review 2026-10-20). */
export function plausibleKpi(value: number, type: KpiColumn['type']): boolean {
  if (!Number.isFinite(value)) return false;
  return type === 'percent' ? value >= 0 && value <= 100 : Math.abs(value) <= MAX_KPI_NUMBER;
}

const inRange = (a: KpiAudit, r: KpiRange) => a.submittedAt !== '' && a.submittedAt >= r.start && a.submittedAt < r.end;

/**
 * Per group (e.g. district or store), per KPI: the average of every visit in
 * the range that recorded it (each visit counts once), the same for the
 * previous range, and the change. Implausible values (plausibleKpi) are
 * left out, so one typo can't skew a district. Groups with no visit in either range are
 * left out.
 */
export function kpiTable<K>(input: {
  audits: readonly KpiAudit[];
  columns: readonly KpiColumn[];
  groupOf: (a: KpiAudit) => K | null;
  range: KpiRange;
  /** null = no comparison (e.g. "All time"). */
  previous: KpiRange | null;
  /** B8: what this visit's value should have been (its budget), or null — enables `KpiCell.budget`. */
  expectedOf?: (a: KpiAudit, c: KpiColumn) => number | null;
}): Map<K, { visits: number; cells: Map<string, KpiCell> }> {
  type Acc = {
    visits: number;
    now: Map<string, number[]>;
    before: Map<string, number[]>;
    budget: Map<string, { value: number; expected: number }[]>;
  };
  const acc = new Map<K, Acc>();
  for (const a of input.audits) {
    const now = inRange(a, input.range);
    const before = !now && input.previous !== null && inRange(a, input.previous);
    if ((!now && !before) || !a.kpis) continue;
    const key = input.groupOf(a);
    if (key === null) continue;
    let g = acc.get(key);
    if (!g) {
      g = { visits: 0, now: new Map(), before: new Map(), budget: new Map() };
      acc.set(key, g);
    }
    if (now) g.visits++;
    const into = now ? g.now : g.before;
    for (const c of input.columns) {
      const v = Object.prototype.hasOwnProperty.call(a.kpis.n, c.id) ? a.kpis.n[c.id] : undefined;
      if (typeof v !== 'number' || !plausibleKpi(v, c.type)) continue;
      const list = into.get(c.id);
      if (list) list.push(v);
      else into.set(c.id, [v]);
      if (now && input.expectedOf) {
        const expected = input.expectedOf(a, c);
        if (expected !== null && Number.isFinite(expected)) {
          const pairs = g.budget.get(c.id);
          if (pairs) pairs.push({ value: v, expected });
          else g.budget.set(c.id, [{ value: v, expected }]);
        }
      }
    }
  }
  const mean = (xs: readonly number[] | undefined) => (xs && xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
  const out = new Map<K, { visits: number; cells: Map<string, KpiCell> }>();
  for (const [key, g] of acc) {
    const cells = new Map<string, KpiCell>();
    for (const c of input.columns) {
      const avg = mean(g.now.get(c.id));
      const prevAvg = input.previous ? mean(g.before.get(c.id)) : null;
      const pairs = g.budget.get(c.id);
      cells.set(c.id, {
        avg,
        count: g.now.get(c.id)?.length ?? 0,
        prevAvg,
        change: avg !== null && prevAvg !== null ? avg - prevAvg : null,
        ...(input.expectedOf
          ? {
              budget: pairs?.length
                ? {
                    avgValue: pairs.reduce((s, p) => s + p.value, 0) / pairs.length,
                    avgExpected: pairs.reduce((s, p) => s + p.expected, 0) / pairs.length,
                    n: pairs.length,
                  }
                : null,
            }
          : {}),
      });
    }
    out.set(key, { visits: g.visits, cells });
  }
  return out;
}

/** "87.4%" / "12.3" — one decimal, trailing ".0" kept off whole numbers. */
export function formatKpi(value: number | null, type: KpiColumn['type']): string {
  if (value === null) return '—';
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return type === 'percent' ? `${text}%` : text;
}

/** "+2.1 pts" / "−3" / "±0" — percent changes are points, not percent-of-percent. */
export function formatKpiChange(change: number | null, type: KpiColumn['type']): string {
  if (change === null) return '';
  const rounded = Math.round(change * 10) / 10;
  const abs = Math.abs(rounded);
  const text = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '±';
  return `${sign}${text}${type === 'percent' ? ' pts' : ''}`;
}

// --- Budget / goal questions (owner ask 2026-10-26) --------------------------------

/**
 * A version's BUDGET / GOAL questions: its number / percent questions ticked
 * "Budget / goal question" in the builder — and only those (owner ruling
 * 2026-10-26, "flags only, from today": a version that ticks none has none,
 * so the DM Visit shows "No budgets or goal questions…" until an admin
 * publishes a version with its KPIs ticked; saved budgets are kept).
 */
export function budgetQuestionIds(def: TemplateDefinition | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const s of def?.sections ?? []) {
    for (const q of s.questions ?? []) if (isKpiType(q.type) && q.budget === true) ids.add(q.id);
  }
  return ids;
}

/**
 * The family's budget / goal columns: kpiColumns() narrowed to the NEWEST
 * non-draft version's budget / goal questions (its wording and order).
 * Budgets and Goals, the KPIs vs budget report, Analytics' budget colours
 * and the store page's budgets use it.
 */
export function budgetColumns(
  rows: readonly { version: number; status?: string; definition: TemplateDefinition | null }[],
): KpiColumn[] {
  const newest = rows.filter((r) => r.status !== 'Draft' && r.definition).sort((a, b) => b.version - a.version)[0];
  if (!newest) return [];
  const ids = budgetQuestionIds(newest.definition);
  return kpiColumns(rows).filter((c) => ids.has(c.id));
}
